import type { ImagePromptRecommendationContext } from "./generateImagePromptFromRecommendation";
import type { CorrectedTextItem } from "@/types/textFix";
import { AuthHttpError } from "@/api/geniBackend";
import { applyTextFixesToImage } from "./textFixOnCanvas";
import { inferImageAspectRatio } from "./imageAspectRatio";
import { recordGenerationStat } from "@/api/accountBilling";
import { orchestrateProductImageEdit, ImageEditFailedError, type ProductImageBillingScope } from "@/api/geniAiOrchestration";
import { useAuthStore } from "@/stores/auth";
import { getActivePinia } from "pinia";
import {
  dismissImageGenToast,
  pushImageGenGeniToast,
  pushImageGenWillSpendToast,
  pushInsufficientGeniForImageGenToast,
} from "@/lib/geniImageGenToast";
import { randomUUID } from "@/lib/randomUUID";

export type GenerateImageStubOptions = ImagePromptRecommendationContext;

export const IMAGE_GEN_MODELS = [{ id: "google/gemini-3.1-flash-image-preview", name: "Nano Banana (Gemini 3.1 Flash Image Preview)" }] as const;

export type ImageGenModelId = (typeof IMAGE_GEN_MODELS)[number]["id"];
export type ImageQuality = "low" | "medium" | "high";
export type ImageSize = "1024x1024" | "1024x1536" | "1536x1024";

// Для будущего шага OCR+LLM: эталонные строки из контекста слайда рекомендации
export function buildTextsListFromContext(context: GenerateImageStubOptions): string[] {
  const { slide, strategy } = context;
  const lines: string[] = [];
  if (slide.goal?.trim()) lines.push(slide.goal.trim());
  if (slide.message?.trim()) lines.push(slide.message.trim());
  if (Array.isArray(slide.proof)) {
    slide.proof.forEach((p) => (p?.trim() ? lines.push(p.trim()) : null));
  }
  if (strategy.coreMessages?.length) {
    strategy.coreMessages.forEach((m) => (m?.trim() ? lines.push(m.trim()) : null));
  }
  if (strategy.mustProve?.length) {
    strategy.mustProve.forEach((m) => (m?.trim() ? lines.push(m.trim()) : null));
  }
  return [...new Set(lines)];
}

export type OpenAiImageEditExtraOptions = {
  input_fidelity?: "high" | "low";
};

function isInsufficientGeniBalanceError(error: unknown): boolean {
  if (!(error instanceof AuthHttpError)) return false;
  if (error.status !== 400 && error.status !== 402) return false;
  return (
    /insufficient geni balance/i.test(error.message) ||
    /not enough geni/i.test(error.message) ||
    /недостаточно.*geni/i.test(error.message) ||
    /geni.*недостаточно/i.test(error.message)
  );
}

/**
 * Постобработка сгенерированного изображения: применяет исправленные текстовые элементы
 * и возвращает data URL изображения с перерисованными надписями.
 */
export async function applyTextFixToImage(imageDataUrl: string, correctedItems: CorrectedTextItem[]): Promise<string> {
  if (!correctedItems.length) {
    return imageDataUrl;
  }
  return applyTextFixesToImage(imageDataUrl, correctedItems);
}

/**
 * Генерация изображения товара — полный пайплайн на сервере (промпты и модели недоступны клиенту).
 */
export async function generateImageStub(
  sourceImageUrl: string,
  userPrompt?: string,
  context?: GenerateImageStubOptions,
  callOptions?: {
    billingScope?: ProductImageBillingScope;
    recordStatEvent?: boolean;
    idempotencyKey?: string;
    /**
     * Suppress all geni toasts and balance updates. Used for silent refresh-resume:
     * the backend serves the already-paid result from cache, so showing "−1 geni" or
     * overwriting the balance with a historical value would be misleading.
     */
    silent?: boolean;
  }
): Promise<string> {
  const hasContext = !!context;
  const hasUserPrompt = !!(userPrompt ?? context?.userPrompt)?.trim();

  if (hasContext || hasUserPrompt) {
    const recommendationContext = hasContext ? { ...context!, userPrompt: undefined } : undefined;
    const billingScope = callOptions?.billingScope ?? "standalone_photo";
    // Caller-provided key is reused across HTTP retries (e.g. pendingPhotoQuickEdits
    // replay after reload) so the backend dedupes the GENI spend.
    const idempotencyKey = callOptions?.idempotencyKey ?? randomUUID();
    const aspectRatio = await inferImageAspectRatio(sourceImageUrl);
    const imageGenToastId = callOptions?.silent ? null : pushImageGenWillSpendToast(1);
    try {
      const result = await orchestrateProductImageEdit(
        {
          sourceImageUrl,
          userIntent: userPrompt ?? context?.userPrompt,
          recommendationContext: recommendationContext as Record<string, unknown> | undefined,
          aspectRatio,
          billingScope,
        },
        idempotencyKey,
      );
      if (!callOptions?.silent) {
        if (result.geniSpent && result.geniSpent > 0) {
          pushImageGenGeniToast(result.geniSpent, result.geniBalance, { id: imageGenToastId ?? undefined });
        } else {
          dismissImageGenToast(imageGenToastId);
        }
      }
      if (callOptions?.recordStatEvent !== false) {
        void recordGenerationStat({
          sessionKind: "image_edit",
          imagesGenerated: 1,
          textsGenerated: 0,
        }).catch(() => {
          /* offline / неавторизован — не блокируем UI */
        });
      }
      return result.url;
    } catch (error) {
      // GC-6: всегда дисмиссаем «Генерация изображения…» тост — он loading-only,
      // сам не закроется. Insufficient — отдельный error-тост (как было).
      // На ImageEditFailedError (бэк ответил {failed, geniBalance}) синхронизируем
      // локальный authStore с фактическим балансом после refund — иначе UI может
      // показывать рассинхронизованное число.
      if (!callOptions?.silent) {
        if (isInsufficientGeniBalanceError(error)) {
          pushInsufficientGeniForImageGenToast({ id: imageGenToastId ?? undefined });
        } else {
          dismissImageGenToast(imageGenToastId);
        }
      }
      if (error instanceof ImageEditFailedError && typeof error.geniBalance === "number") {
        if (getActivePinia()) {
          useAuthStore().setGeniBalance(Math.max(0, error.geniBalance));
        }
      }
      throw error;
    }
  }

  return new Promise((resolve) => {
    setTimeout(() => resolve(sourceImageUrl), 5000);
  });
}
